import format from "date-fns/format";
import isValid from "date-fns/isValid";

/**
 * Formats a UTC‐based ISO string into Eastern Time, using whichever pattern you ask for.
 *
 * @param {string} isoString         – The ISO datetime (UTC) you received.
 * @param {string} [formatKey="datetime"]
 *    • "datetime" → "M/d/yyyy h:mm a"
 *    • "time"     → "h:mm a"
 *    • "date"     → "M/d/yyyy"
 *    • Anything else is treated as a custom date-fns pattern.
 *
 * @returns {string}  – e.g. "6/5/2025 1:02 PM", "1:02 PM", "6/5/2025", etc.
 *                     Returns "" if the ISO is invalid.
 */
export const formatDateTime = (isoString, formatKey = "datetime") => {
  if (!isoString) return "";

  try {
    // Ensure we pass a true UTC moment into convertToEasternTime:
    const normalizedIso = normalizeIsoStringToUtc(isoString);
    const easternDate = convertToEasternTime(normalizedIso);
    if (!easternDate) {
      console.warn("Invalid date:", isoString);
      return "";
    }

    // Pre‐baked patterns:
    const formatMap = {
      datetime: "M/d/yyyy h:mm a",
      time: "h:mm a",
      date: "M/d/yyyy",
    };
    const pattern = formatMap[formatKey] || formatKey;
    return format(easternDate, pattern);
  } catch (err) {
    console.error("Error formatting date:", err);
    return "";
  }
};

/**
 * If the incoming ISO string does NOT already include a “Z” or a numeric offset,
 * we’ll append “Z” so that JS/Date parses it as UTC.
 *
 * E.g. "2025-06-05T17:02:08.420000" → "2025-06-05T17:02:08.420000Z"
 *      "2025-06-05T17:02:08.420000-04:00" → (unchanged)
 *      "2025-06-05T17:02:08.420000Z" → (unchanged)
 */
export const normalizeIsoStringToUtc = (iso) => {
  // If it already has a 'Z' or ends with ±hh:mm, leave it alone.
  // Otherwise, tack on 'Z' so Date(...) sees it as UTC.
  if (/[Zz]$/.test(iso) || /[+\-]\d\d:\d\d$/.test(iso)) {
    return iso;
  }
  return iso + "Z";
};

/**
 * Epoch ms for a backend TIMESTAMP. LOS writes naive UTC wall time
 * (`dt.datetime.now().isoformat()`, no offset); a bare `new Date(naive)` reads
 * that as the VIEWER's local time and shifts it by the UTC offset. This reads a
 * naive string as UTC. Returns null when missing/unparseable.
 */
export const timestampMs = (value) => {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isNaN(t) ? null : t;
  }
  if (typeof value === "number") return value;
  const s = String(value);
  // A bare "YYYY-MM-DD" already parses as UTC midnight; "…Z" would not parse.
  const t = new Date(
    /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : normalizeIsoStringToUtc(s),
  ).getTime();
  return Number.isNaN(t) ? null : t;
};

/**
 * Takes a UTC‐normalized ISO string (or any JS‐Date‐parsable UTC string)
 * and returns a JS Date object that represents the same instant in America/New_York.
 *
 * @param {string} utcIsoString
 * @returns {Date|null}
 */
export const convertToEasternTime = (utcIsoString) => {
  // Parse as UTC because we appended 'Z' if needed:
  const utcDate = new Date(utcIsoString);
  if (!isValid(utcDate)) {
    return null;
  }

  // Use Intl.DateTimeFormat to pull out the year/month/day/hour/minute/second
  // in Eastern Time.
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: false,
  });

  // Example output: "6/5/2025, 13:02:08"
  const easternStr = formatter.format(utcDate);
  const [datePart, timePart] = easternStr.split(", ");
  const [month, day, year] = datePart.split("/");
  const [hour, minute, second] = timePart.split(":");

  // Note: JS Date months are 0‐indexed.
  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    // Some engines render midnight as "24" under hour12:false, which would
    // roll the Date into the next day — fold it back to 0.
    Number(hour) % 24,
    Number(minute),
    Number(second)
  );
};
